import type { KasseStatus } from '../api';
import type { BedienerRechte } from '../bediener';
import {
  berechneErstattung,
  type ErstattungsErgebnis,
  type OriginalPosition,
  type RetoureAuswahl,
} from './berechnung';

/**
 * Retourenentwurf der Kasse (Stufe 1): reiner Zustand ohne Seiteneffekte. Nichts davon wird
 * gespeichert oder gesendet – der Abschluss ist gesperrt, bis RF-1 (Belegtyp), RF-2
 * (USt-Berichtigung), RF-4 (Belegangaben) und E-SB-1 (Storno-Referenz, Migration 0085)
 * freigegeben sind.
 */
export const ABSCHLUSS_GESPERRT_TEXT = 'Abschluss erst nach Freigabe RF-1/RF-2/RF-4 und E-SB-1';

export type Schritt = 'beleg' | 'artikel' | 'grund' | 'erstattung' | 'abschluss';

export const SCHRITTE: readonly { id: Schritt; text: string }[] = [
  { id: 'beleg', text: 'Beleg finden' },
  { id: 'artikel', text: 'Artikel' },
  { id: 'grund', text: 'Grund und Zustand' },
  { id: 'erstattung', text: 'Erstattung' },
  { id: 'abschluss', text: 'Abschluss' },
];

export type Grund =
  'gefaellt_nicht' | 'falsche_groesse' | 'doppelt' | 'geschenk' | 'mangel' | 'sonstiges';

export const GRUENDE: readonly { id: Grund; text: string }[] = [
  { id: 'gefaellt_nicht', text: 'Gefällt nicht' },
  { id: 'falsche_groesse', text: 'Falsche Größe oder Sorte' },
  { id: 'doppelt', text: 'Doppelt gekauft' },
  { id: 'geschenk', text: 'Geschenk' },
  { id: 'mangel', text: 'Mangel / Gewährleistung' },
  { id: 'sonstiges', text: 'Sonstiges' },
];

export type Zustand = 'verfuegbar' | 'pruefung' | 'beschaedigt' | 'defekt' | 'nicht_zurueck';

/**
 * Artikelzustände (Fachkonzept 9). Stufe 1 laut Entscheidungsvorlage: wiederverkaufsfähig, zur
 * Prüfung, nicht zurück ins Lager; „beschädigt“ und „defekt“ (Sperrbestand) sind spätere
 * Ausbaustufe und nur sichtbar, nicht wählbar.
 */
export const ZUSTAENDE: readonly {
  id: Zustand;
  text: string;
  wirkung: string;
  stufe1: boolean;
}[] = [
  {
    id: 'verfuegbar',
    text: 'Wiederverkaufsfähig',
    wirkung: 'zurück in den verfügbaren Bestand',
    stufe1: true,
  },
  {
    id: 'pruefung',
    text: 'Zur Prüfung',
    wirkung: 'Prüfbestand – nicht verkaufbar, bis geprüft',
    stufe1: true,
  },
  { id: 'beschaedigt', text: 'Beschädigt', wirkung: 'Sperrbestand', stufe1: false },
  {
    id: 'defekt',
    text: 'Defekt',
    wirkung: 'Sperrbestand, Rücksendung an Lieferanten möglich',
    stufe1: false,
  },
  {
    id: 'nicht_zurueck',
    text: 'Nicht zurück ins Lager',
    wirkung: 'keine Bestandsbuchung, nur Vermerk',
    stufe1: true,
  },
];

/** Bei „Mangel / Gewährleistung“ ist Ware nie sofort wieder verkaufbar (Fachkonzept 13). */
export function zustandWaehlbar(zustand: Zustand, grund: Grund | null): boolean {
  const z = ZUSTAENDE.find((x) => x.id === zustand);
  if (!z?.stufe1) {
    return false;
  }
  return !(grund === 'mangel' && zustand === 'verfuegbar');
}

/** Freigabegrenze (Vorschlag E-8, noch nicht je Unternehmer konfiguriert). */
export const FREIGABE_AB_CENT = 5000;

export interface PositionsAngabe {
  grund: Grund | null;
  notiz: string;
  zustand: Zustand | null;
}

export const LEERE_ANGABE: PositionsAngabe = { grund: null, notiz: '', zustand: null };

export interface Entwurf {
  /** Gewählte Menge je Positionsindex (0 = nicht gewählt). */
  auswahl: ReadonlyMap<number, number>;
  angaben: ReadonlyMap<number, PositionsAngabe>;
}

export const LEERER_ENTWURF: Entwurf = { auswahl: new Map(), angaben: new Map() };

export function auswahlListe(e: Entwurf): RetoureAuswahl[] {
  return [...e.auswahl]
    .filter(([, menge]) => menge > 0)
    .sort(([a], [b]) => a - b)
    .map(([index, menge]) => ({ index, menge }));
}

export function setzeMenge(e: Entwurf, index: number, menge: number): Entwurf {
  const auswahl = new Map(e.auswahl);
  const angaben = new Map(e.angaben);
  if (menge <= 0) {
    auswahl.delete(index);
    angaben.delete(index);
  } else {
    auswahl.set(index, menge);
    if (!angaben.has(index)) {
      angaben.set(index, LEERE_ANGABE);
    }
  }
  return { auswahl, angaben };
}

export function setzeAngabe(e: Entwurf, index: number, teil: Partial<PositionsAngabe>): Entwurf {
  const angaben = new Map(e.angaben);
  const neu = { ...(angaben.get(index) ?? LEERE_ANGABE), ...teil };
  // Grundwechsel auf „Mangel“: ein nicht mehr zulässiger Zustand wird zurückgesetzt
  if (neu.zustand !== null && !zustandWaehlbar(neu.zustand, neu.grund)) {
    neu.zustand = null;
  }
  angaben.set(index, neu);
  return { auswahl: e.auswahl, angaben };
}

export function vorschau(
  positionen: readonly OriginalPosition[],
  e: Entwurf,
  bisherZurueck: ReadonlyMap<number, number>,
): ErstattungsErgebnis {
  return berechneErstattung(positionen, auswahlListe(e), bisherZurueck);
}

/** Was fehlt noch für „Grund und Zustand“? Leer = vollständig. */
export function fehlendeAngaben(e: Entwurf, bezeichnung: (index: number) => string): string[] {
  const fehlt: string[] = [];
  for (const { index } of auswahlListe(e)) {
    const a = e.angaben.get(index) ?? LEERE_ANGABE;
    const name = bezeichnung(index);
    if (a.grund === null) {
      fehlt.push(`${name}: Rückgabegrund fehlt`);
    } else if (a.grund === 'sonstiges' && a.notiz.trim() === '') {
      fehlt.push(`${name}: Notiz ist bei „Sonstiges“ Pflicht`);
    }
    if (a.zustand === null) {
      fehlt.push(`${name}: Artikelzustand fehlt`);
    }
  }
  return fehlt;
}

/** Hinweise, die eine Freigabe verlangen würden (RU-5, noch nicht verfügbar). */
export function freigabeHinweise(e: Entwurf, summeCent: number): string[] {
  const h: string[] = [];
  if (summeCent >= FREIGABE_AB_CENT) {
    h.push('Erstattung ab 50,00 € – Freigabe durch die Filialleitung nötig (Vorschlag E-8)');
  }
  if ([...e.angaben.values()].some((a) => a.grund === 'mangel')) {
    h.push('Mangel / Gewährleistung – Freigabe durch die Filialleitung nötig');
  }
  return h;
}

/**
 * Vorbereitungsrecht der Kasse (nur Anzeige): Bis die Rechte `retoure`/`retoure_abschluss`/
 * `retoure_freigabe` freigegeben sind (RU-5, `packages/api/src/retoure-rechte.ts`), lehnt sich
 * die Vorbereitung an das bestehende Stornorecht („Stornieren und Rückerstatten“) an. Die
 * serverseitige Durchsetzung ist Teil der späteren Freigabe.
 */
export function vorbereitungErlaubt(rechte: Pick<BedienerRechte, 'storno'>): boolean {
  return rechte.storno;
}

/* ── Kassenzustand (Codex-Prüfung M2, Testmatrix T21/RT-11) ────────────────────── */

/** Stand der Statusabfrage `GET /kassen/:id/status` (nur lesend). */
export type SystemStand =
  | { art: 'pruefung' }
  | { art: 'fehler' }
  | {
      art: 'ok';
      status: Pick<KasseStatus, 'status' | 'ausfallAktiv' | 'letzterBeleg' | 'schlussbeleg'>;
      /** Zeitpunkt der Antwort (ms seit Epoche); danach gilt der Stand nur begrenzt. */
      geprueftAm: number;
    };

/** Wie oft der Kassenstatus während eines offenen Retourenablaufs neu geprüft wird. */
export const STATUS_PRUEFUNG_MS = 30_000;

/**
 * Codex Runde 2 (M2a): So lange gilt eine gesunde Statusantwort. Danach ist der Stand unbekannt
 * und damit gesperrt, bis eine neue Antwort da ist (z. B. gedrosselte Zeitgeber im Hintergrund).
 */
export const STATUS_GUELTIG_MS = 45_000;

/**
 * Sperrgrund aus dem Kassenzustand, null = frei. Unbekannt heißt gesperrt: Retouren nur bei
 * funktionierender Sicherheitseinrichtung (Fachkonzept 10.3, Vorschlag zu RF-7), nicht während
 * eines Ausfalls, nicht nach der Außerbetriebnahme und nicht, solange die Kasse selbst gesperrt
 * ist (offline gepufferte Belege, Verkaufsstopp, ungeklärter Verkauf, Anmeldung).
 */
export function systemSperre(
  stand: SystemStand,
  kassenSperre: string | null,
  jetzt: number,
): string | null {
  if (kassenSperre !== null) {
    return kassenSperre;
  }
  if (stand.art === 'pruefung') {
    return 'Kassenstatus wird geprüft – Retoure bis dahin gesperrt.';
  }
  if (stand.art === 'fehler') {
    return 'Kassenstatus unbekannt – Retoure gesperrt. Verbindung und Status prüfen.';
  }
  if (jetzt - stand.geprueftAm > STATUS_GUELTIG_MS) {
    return 'Kassenstatus veraltet – Retoure gesperrt, bis der Status neu geprüft ist.';
  }
  const s = stand.status;
  if (s.ausfallAktiv || s.letzterBeleg?.signaturGestoert === true) {
    return 'Retoure derzeit nicht möglich – Sicherheitseinrichtung ausgefallen.';
  }
  if (s.status !== 'aktiv' || s.schlussbeleg !== null) {
    return 'Kasse außer Betrieb – keine Retoure.';
  }
  return null;
}

/**
 * Sperre aus dem, was die Kasse (App.tsx) schon weiß – ohne neuen Abruf. Reihenfolge nach
 * Schwere; der erste Grund wird angezeigt.
 */
export function kassenSperreFuerRetoure(z: {
  anmeldung: string;
  verkaufsstopp: string | null;
  offlineGepuffert: number;
  ungeklaert: number;
}): string | null {
  if (z.anmeldung !== 'ok') {
    return 'Kassenanmeldung ungültig bzw. Server-Sperre – Retoure gesperrt.';
  }
  if (z.verkaufsstopp !== null) {
    return 'Verkaufsstopp aktiv – Retoure gesperrt.';
  }
  if (z.offlineGepuffert > 0) {
    return 'Offline gepufferte Belege noch nicht übertragen (Ausfall) – Retoure gesperrt bis zur Synchronisation.';
  }
  if (z.ungeklaert > 0) {
    return 'Ein Verkauf ist noch ungeklärt – Retoure gesperrt bis zur Klärung.';
  }
  return null;
}
